import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AllDocumentsService } from '../services/allDocumentsService';
import { DocumentTreeItem, TreeItemType } from '../types/models';
import { EmployeeService } from '../services/employeeService';
import { api, apiHelpers } from '../services/api';

interface TreeNodeProps {
  item: DocumentTreeItem;
  level: number;
}

const TreeNode: React.FC<TreeNodeProps> = ({ item, level }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = item.children && item.children.length > 0;
  const hasUrl = item.url && item.url.trim() !== '';

  // Нормализуем тип, проверяя enum значения
  const normalizeType = (type: TreeItemType | string | number): TreeItemType => {
    // Проверяем числовые значения enum
    if (type === TreeItemType.InstructionFolder || type === 0 || type === 'InstructionFolder' || type === '0') {
      return TreeItemType.InstructionFolder;
    }
    if (type === TreeItemType.ProbationFolder || type === 1 || type === 'ProbationFolder' || type === '1') {
      return TreeItemType.ProbationFolder;
    }
    if (type === TreeItemType.InstructionItem || type === 2 || type === 'InstructionItem' || type === '2') {
      return TreeItemType.InstructionItem;
    }
    if (type === TreeItemType.ProbationItem || type === 3 || type === 'ProbationItem' || type === '3') {
      return TreeItemType.ProbationItem;
    }
    // Проверяем, является ли значение одним из enum значений
    if (Object.values(TreeItemType).includes(type as TreeItemType)) {
      return type as TreeItemType;
    }
    // По умолчанию возвращаем InstructionItem
    return TreeItemType.InstructionItem;
  };

  const itemType = normalizeType(item.type);

  const getIcon = (type: TreeItemType) => {
    switch (type) {
      case TreeItemType.InstructionFolder:
        return '📁'; // Папка инструктажей
      case TreeItemType.ProbationFolder:
        return '📂'; // Папка стажировок
      case TreeItemType.InstructionItem:
        return '📄'; // Инструктаж
      case TreeItemType.ProbationItem:
        return '📄'; // Стажировка
      default:
        return '📄';
    }
  };

  const getTypeLabel = (type: TreeItemType) => {
    // Не отображаем текстовые метки типов
    return '';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Если есть URL, не сворачиваем/разворачиваем папку
    if (hasUrl) {
      return;
    }
    // Если есть дети, переключаем состояние
    if (hasChildren) {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    }
  };

  const normalizeUrl = (url: string): string => {
    // Если URL уже абсолютный (начинается с http:// или https://), возвращаем как есть
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Если URL начинается с /, возвращаем как есть
    if (url.startsWith('/')) {
      return url;
    }
    // Иначе добавляем / в начало для абсолютного пути
    return `/${url}`;
  };

  const handleUrlClick = async (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      const normalizedUrl = normalizeUrl(url);
      
      // Добавляем companyId к URL, если нужно
      const urlWithCompanyId = apiHelpers.addCompanyIdToUrl(normalizedUrl);

      // Выполняем POST запрос с blob response
      const response = await api.post(urlWithCompanyId, {}, {
        responseType: 'blob',
      });

      // Создаем временную ссылку для скачивания файла
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Пытаемся получить имя файла из заголовков ответа
      const contentDisposition = response.headers['content-disposition'];
      let filename = item.name;
      if (contentDisposition) {
        // Сначала проверяем filename*=UTF-8'' формат (RFC 5987) — содержит корректную кириллицу
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/);
        if (filenameStarMatch && filenameStarMatch[1]) {
          filename = decodeURIComponent(filenameStarMatch[1]);
        } else {
          // Fallback на старый формат filename=
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''));
          }
        }
      }
      
      // Убеждаемся, что файл имеет расширение .docx
      if (!filename.toLowerCase().endsWith('.docx')) {
        // Удаляем существующее расширение, если есть
        const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
        filename = `${nameWithoutExt}.docx`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Ошибка при загрузке файла:', error);
      alert('Ошибка при загрузке файла');
    }
  };

  const nameContent = hasUrl ? (
    <span
      style={{
        color: '#007bff',
        cursor: 'pointer',
        textDecoration: 'underline',
        flex: 1,
      }}
      onClick={(e) => handleUrlClick(e, item.url!)}
    >
      {item.name}
    </span>
  ) : (
    <span style={{ flex: 1 }}>{item.name}</span>
  );

  return (
    <div style={{ marginLeft: `${level * 20}px` }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 0',
          cursor: hasChildren || hasUrl ? 'pointer' : 'default',
          userSelect: 'none',
        }}
        onClick={handleClick}
      >
        {hasChildren && (
          <span style={{ marginRight: '8px', fontSize: '12px' }}>
            {isExpanded ? '▼' : '▶'}
          </span>
        )}
        {!hasChildren && <span style={{ marginRight: '24px' }} />}
        <span style={{ marginRight: '8px', fontSize: '16px' }}>
          {getIcon(itemType)}
        </span>
        {nameContent}
        {item.date && (
          <span style={{ color: '#666', fontSize: '14px' }}>
            {formatDate(item.date)}
          </span>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {item.children!.map((child) => (
            <TreeNode key={child.id} item={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

export const EmployeeDocumentsPage: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<DocumentTreeItem[]>([]);
  const [employeeName, setEmployeeName] = useState<string>('');

  useEffect(() => {
    if (employeeId) {
      loadDocuments();
      loadEmployeeName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  // Нормализуем тип элемента дерева, проверяя enum значения
  const normalizeTreeItemType = (type: any): TreeItemType => {
    // Проверяем числовые значения enum
    if (type === TreeItemType.InstructionFolder || type === 0 || type === 'InstructionFolder' || type === '0') {
      return TreeItemType.InstructionFolder;
    }
    if (type === TreeItemType.ProbationFolder || type === 1 || type === 'ProbationFolder' || type === '1') {
      return TreeItemType.ProbationFolder;
    }
    if (type === TreeItemType.InstructionItem || type === 2 || type === 'InstructionItem' || type === '2') {
      return TreeItemType.InstructionItem;
    }
    if (type === TreeItemType.ProbationItem || type === 3 || type === 'ProbationItem' || type === '3') {
      return TreeItemType.ProbationItem;
    }
    // Проверяем, является ли значение одним из enum значений
    if (Object.values(TreeItemType).includes(type)) {
      return type as TreeItemType;
    }
    // По умолчанию возвращаем InstructionItem
    return TreeItemType.InstructionItem;
  };

  // Рекурсивно нормализуем все элементы дерева
  const normalizeTreeItem = (item: any): DocumentTreeItem => {
    const normalized: DocumentTreeItem = {
      ...item,
      type: normalizeTreeItemType(item.type),
    };
    
    if (item.children && Array.isArray(item.children)) {
      normalized.children = item.children.map((child: any) => normalizeTreeItem(child));
    }
    
    return normalized;
  };

  const loadDocuments = async () => {
    if (!employeeId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await AllDocumentsService.getAllByEmployee(Number(employeeId));

      console.log('API Response:', response);
      console.log('Response data:', response.data);

      if (response.success && response.data) {
        // Проверяем разные возможные структуры ответа
        let documentsData: any[] = [];
        let name = '';

        // Если ответ - объект с полем documents
        if (Array.isArray((response.data as any).documents)) {
          documentsData = (response.data as any).documents;
          name = (response.data as any).employeeName || '';
        }
        // Если ответ - массив документов напрямую
        else if (Array.isArray(response.data)) {
          documentsData = response.data as any[];
        }
        // Если ответ - объект EmployeeDocumentsResponse
        else if ((response.data as any).documents) {
          documentsData = (response.data as any).documents;
          name = (response.data as any).employeeName || '';
        }

        // Нормализуем типы всех элементов дерева
        const normalizedDocuments = documentsData.map(item => normalizeTreeItem(item));

        console.log('Parsed documents:', normalizedDocuments);
        setDocuments(normalizedDocuments);
        if (name) {
          setEmployeeName(name);
        }
      } else {
        setError(response.message || 'Ошибка загрузки документов');
      }
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeName = async () => {
    if (!employeeId) return;

    try {
      const response = await EmployeeService.getById(Number(employeeId));
      if (response.success && response.data) {
        const employee = response.data;
        const fullName = `${employee.surname} ${employee.name} ${employee.patronymic}`.trim();
        setEmployeeName(fullName);
      }
    } catch (err) {
      // Игнорируем ошибку, имя загрузится из ответа API документов
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/employees')}>
          Вернуться к списку сотрудников
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 className="page-title">
            Документы сотрудника: {employeeName || `ID: ${employeeId}`}
          </h1>
          <button className="btn btn-secondary" onClick={() => navigate('/employees')}>
            Вернуться к списку сотрудников
          </button>
        </div>
      </div>

      <div className="card">
        {documents.length === 0 ? (
          <div>
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              У сотрудника нет документов
            </div>
            {/* Временный блок для отладки - можно удалить после проверки */}
            <details style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Отладочная информация</summary>
              <pre style={{ marginTop: '10px', fontSize: '12px', overflow: 'auto' }}>
                {JSON.stringify({ documents, employeeName, employeeId }, null, 2)}
              </pre>
            </details>
          </div>
        ) : (
          <div>
            {documents.map((item) => (
              <TreeNode key={item.id} item={item} level={0} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

